import { React, useState, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../../services/auth.service';
import { Alert } from 'antd';
import { CompanyLogo } from '../../components/companyLogo';
import { Eye, EyeSlash } from 'react-bootstrap-icons';
import { PasswordSecurityCheck, checkPasswordStrength } from '../../components/passwordSecurityCheck';
import localStorageService from '../../services/localStorage.service';
import '../../styles/auth.scss';

const ResetPassLink = () => {
    return (
        <a href='/password-reset' className="auth-link">password reset</a>
    );
};

export const PasswordChange = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const token = new URLSearchParams(location.search).get('token');
    const { passwordChange } = useContext(AuthContext);
    const [showPassword1, setShowPassword1] = useState(false);
    const [showPassword2, setShowPassword2] = useState(false);

    const [formData, setFormData] = useState({
        password1: '',
        password2: '',
        isPassword1Secure: false,
        isPassword2Secure: false
    });

    const [showAlert, setShowAlert] = useState(false);
    const [alertData, setAlertData] = useState({
        heading: '',
        body: '',
    });

    const handlePasswordChange = (e) => {
        const newPassword = e.target.value;
        const fieldId = e.target.id;
        
        // Check password strength
        const isSecure = checkPasswordStrength(newPassword);

        setFormData({...formData, [e.target.name]: newPassword, [fieldId]: isSecure});
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if(formData.password1 === formData.password2) {
            const userData = {
                "token": token,
                "password": formData.password1,
            };

            const response = await passwordChange(userData);
    
            if (response?.result) {   
                localStorageService.clearLocalStorage();
                navigate("/login");
            } else if (response?.token === 'invalid' || response?.token === 'not found') {
                setAlertData({
                    heading: 'Invalid Token!',
                    body: `The password reset link was invalid, possibly because it has already been used or it is expired. Please request a new `,
                });
                setShowAlert(true);
            }
        } else {
            setAlertData({
                heading: 'Mismatched passwords!',
                body: 'Passwords do not match, please try again!',
            });
            setShowAlert(true);
            setFormData({
                password1: '',
                password2: '',
                isPassword1Secure: false,
                isPassword2Secure: false
            });
        }
    };

    return (
        <div className='auth-container change-password-page'>
            <div className="bottom-circle"></div>
            <div className="auth-form-container">
                <div className="text-center mb-4">
                    <CompanyLogo />
                </div>

                <h1 className='auth-heading text-center'>Change your password</h1>
                
                {showAlert && 
                    <Alert
                        message={alertData.heading}
                        description={
                            <div>
                                <p>{alertData.body}</p>
                                {alertData.heading === 'Invalid Token!' && 
                                    <ResetPassLink />
                                }
                            </div>
                        }
                        type="error"
                        showIcon
                        className="auth-alert"
                    />
                }

                <form method="POST" className="mt-4" onSubmit={handleSubmit}>
                    <div className="password-field-container">
                        <PasswordSecurityCheck 
                            password={formData.password1} 
                            isPasswordSecure={formData.isPassword1Secure}
                        />
                        <div className="auth-input-wrapper">
                            <input
                                autoComplete="current-password"
                                className="auth-input"
                                required
                                id="isPassword1Secure"
                                placeholder="Password"
                                name="password1"
                                onChange={handlePasswordChange}
                                value={formData.password1}
                                type={showPassword1 ? "text" : "password"}
                            />
                            <label className='eye-slash-icon' htmlFor="check">
                                {showPassword1 ? (
                                    <Eye />
                                ) : (
                                    <EyeSlash />
                                )}
                            </label>
                            <input
                                id="check"
                                type="checkbox"
                                value={showPassword1}
                                onChange={() => setShowPassword1((prev) => !prev)}
                                style={{ display: 'none' }}
                            />
                        </div>
                    </div>
                    
                    <div className="password-field-container">
                        <PasswordSecurityCheck 
                            password={formData.password2} 
                            isPasswordSecure={formData.isPassword2Secure}
                        />
                        <div className="auth-input-wrapper">
                            <input
                                autoComplete="current-password"
                                className="auth-input"
                                required
                                id="isPassword2Secure"
                                placeholder="Confirm password"
                                name="password2"
                                onChange={handlePasswordChange}
                                value={formData.password2}
                                type={showPassword2 ? "text" : "password"}
                            />
                            <label className='eye-slash-icon' htmlFor="check2">
                                {showPassword2 ? (
                                    <Eye />
                                ) : (
                                    <EyeSlash />
                                )}
                            </label>
                            <input
                                id="check2"
                                type="checkbox"
                                value={showPassword2}
                                onChange={() => setShowPassword2((prev) => !prev)}
                                style={{ display: 'none' }}
                            />
                        </div>
                    </div>
                    
                    <div className="mt-4">
                        <button 
                            type="submit"
                            className="auth-button" 
                            disabled={
                                !formData.isPassword1Secure ||
                                !formData.isPassword2Secure ||
                                formData.password1 !== formData.password2
                            }
                        >
                            Submit
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};